// src/App.js
import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, Link, Navigate } from "react-router-dom";
import { useTeam } from "./context/TeamContext";
import { getRouteMountKey } from "./utils/routeMountKey";
import "./App.css";
import WebTeamAppearance from './theme/WebTeamAppearance';

// import MobileWarning from "./components/core-components/MobileWarning";
// import "./components/core-components/MobileWarning.css";

import Topbar from "./components/core-components/Topbar";
import Sidebar from "./components/core-components/Sidebar";
import PayerActionOverlay from "./components/core-components/PayerActionOverlay";
import HighlightModeToggle, { isValidHighlightMode } from "./components/core-components/HighlightModeToggle";

import PrivacyBanner from "./components/core-components/PrivacyBanner";
import PrivacyPolicy from "./core-pages/Privacy-Policy";

import TermsOfService from "./core-pages/TermsOfService";
import RiotReviewPage from "./core-pages/RiotReviewPage";
import DesktopDownloadPage from "./core-pages/DesktopDownloadPage";
import PublicClipsPage from "./core-pages/PublicClipsPage";
import PublicClipDetailPage from "./core-pages/PublicClipDetailPage";
import TeamDashboardPage from "./core-pages/TeamDashboardPage";
import AdminStratsPlannerPage from "./core-pages/AdminStratsPlannerPage";
import PrepCenterPage from "./core-pages/PrepCenterPage";
import {TEAM_PREPARATION_PAGES,LEGACY_PREP_PATHS} from "./components/team-center/teamPreparationPages";
import {TeamHubRedirect} from "./components/team-center/TeamHubRedirect";

import { AuthProvider, useAuth } from "./context/AuthContext";
import { HighlightProvider } from "./context/HighlightContext";
import { BlurProvider } from "./context/BlurContext";
import { ZoomProvider } from "./context/ZoomContext";
import { TeamProvider } from "./context/TeamContext";
import { AccessProvider } from "./context/AccessContext";
import {
  RequireAuthPage,
  RequireTeamMembershipPage,
  RequireTeamFeaturePage,
} from "./components/access/RouteAccess";

// Utilities
import { applyInitialTheme, initializeAuthListener } from "./Security/Auth";

// Pages
import Home from "./core-pages/Home";
import SocialsPage from "./core-pages/SocialsPage";
// import Index from "../Archived/IndexPage";
import Login from "./core-pages/Login";
import Signup from "./core-pages/Signup";
import Account2 from "./core-pages/Account2";
import Billing from "./core-pages/Billing/Billing";
import HomeWorkspace from "./core-pages/OverviewPage";
import HomeWorkspaceRedirect from "./components/overview/HomeWorkspaceRedirect";
import {LEGACY_HOME_PATHS} from "./utils/homeWorkspaceRoute";
import JoinTeam from "./components/core-components/JoinTeam";
import MyPrepPage from "./core-pages/MyPrepPage";
import DesktopGoogleSignIn from "./core-pages/DesktopGoogleSignIn";
import VerifyEmail from "./core-pages/VerifyEmail";

import Feedback from "./core-pages/Feedback";
import LFGPage from "./core-pages/LFG/LFGPage";

// Individual playbook pages
import BindMapApproach from "./individual-playbook/Bind/BInd-Map-Approach";
import BindGekko from "./individual-playbook/Bind/Bind-Gekko";
import CorrodeYoru from "./individual-playbook/Corrode/Corrode-Yoru";
import HavenKJ from "./individual-playbook/Haven/Haven-KJ";
import SplitMapApproach from "./individual-playbook/Split/Split-Map-Approach";

// Coordinated Playbook
import NRG2026BindComp from "./coordinated-playbook/Bind/NRG-2026-Bind-Comp";

import SplitSENComp from "./coordinated-playbook/Split/Split-SEN-Comp";
import TemplateChapter from"./coordinated-playbook/TemplateChapter";

// Core
import GuildeLibrary from "./core-pages/GuideLibrary";
import ReviewDashboard from "./core-pages/ReviewDashboard";
import TeamLibraryPage from './components/team-library/TeamLibraryPage';
import SharedContentLauncher from './components/team-library/SharedContentLauncher';
import TeamStatisticsWorkspace from "./components/planning/TeamStatisticsWorkspace";
import TeamStatsPage from "./core-pages/team-stats/TeamStatsPage";
import TeamStatsSummary from "./core-pages/team-stats/TeamStatsSummary";


import PickUsername from "./core-pages/PickUsername";

// Subscription
import TeamSetupBilling from "./core-pages/Billing/TeamSetupBilling";
import TeamSetupPayers from "./core-pages/Billing/TeamSetupPayers";
const YouTubeWorkspace=React.lazy(()=>import('./components/youtube-test/YouTubeWorkspace'));
const TeamHubPage=React.lazy(()=>import("./components/team-center/TeamHubPage"));
// import TeamSetupPayment from "./core-pages/Billing/TeamSetupPayment";

// Routes that should NOT show Topbar or Sidebar
const CHROMELESS_ROUTES = ["/open","/lpd2", "/lpd3", "/riot-review", "/admin/strats-planner", "/desktop-auth/google"];
const isSocialsPath = (pathname) => pathname.replace(/\/+$/, "").toLowerCase() === "/socials";


function App() {
  const [highlightMode, setHighlightMode] = useState(() => {
    const storedMode = sessionStorage.getItem("highlightMode") || "both";
    return isValidHighlightMode(storedMode) ? storedMode : "both";
  });


  useEffect(() => {
    applyInitialTheme();
    initializeAuthListener();
  }, []);

  const handleHighlightModeChange = useCallback((mode) => {
    const nextMode = isValidHighlightMode(mode) ? mode : "both";
    sessionStorage.setItem("highlightMode", nextMode);
    setHighlightMode(nextMode);
  }, []);


  return (
    <ZoomProvider>
      <AuthProvider>
        <TeamInitializerWrapper>
          <AccessProvider>
            <HighlightProvider highlightSource={highlightMode}>
              <Router>
                <BlurProvider>
                  <div className="App">
                    <ConditionalTopbar
                      highlightMode={highlightMode}
                      onHighlightModeChange={handleHighlightModeChange}
                    />
                    <ConditionalSidebar />
                    <ConditionalGuideHighlightToggle
                      highlightMode={highlightMode}
                      onHighlightModeChange={handleHighlightModeChange}
                    />
                    <ConditionalPayerActionOverlay />
                    <MainContent />
                    <PrivacyBanner />

                    {/* <MobileWarning minWidth={900} /> */}
                    {/* or UA-based: <MobileWarning useUA /> */}
                  </div>
                </BlurProvider>
              </Router>
            </HighlightProvider>
          </AccessProvider>
        </TeamInitializerWrapper>
      </AuthProvider>
    </ZoomProvider>
  );
}

// src/App.js
function TeamInitializerWrapper({ children }) {
  const { userData } = useAuth();

  return (
    <TeamProvider initialTeamId={userData?.activeTeamId ?? null}>
      <WebTeamAppearance/>
      {children}

    </TeamProvider>
  );
}

// Only hide Topbar on selected paths
function ConditionalTopbar({ highlightMode, onHighlightModeChange }) {
  const location = useLocation();
  if (isSocialsPath(location.pathname)) return null;
  if (CHROMELESS_ROUTES.includes(location.pathname)) return null;
  return <Topbar highlightMode={highlightMode} onHighlightModeChange={onHighlightModeChange} />;
}


function ConditionalSidebar() {
  const location = useLocation();
  if (isSocialsPath(location.pathname)) return null;
  if (CHROMELESS_ROUTES.includes(location.pathname)) return null;
  return <Sidebar />;
}

function ConditionalPayerActionOverlay() {
  const location = useLocation();
  return isSocialsPath(location.pathname) || location.pathname === "/desktop-auth/google" ? null : <PayerActionOverlay />;
}

const GUIDE_HIGHLIGHT_ROUTES = new Set([
  "/Bind-Map-Macro",
  "/Bind-Gekko",
  "/Split-Map-Macro",
  "/Haven-KJ",
  "/Corrode-Yoru",
]);

function isGuideHighlightRoute(pathname) {
  return GUIDE_HIGHLIGHT_ROUTES.has(pathname) || pathname.startsWith("/playbook/");
}

function ConditionalGuideHighlightToggle({ highlightMode, onHighlightModeChange }) {
  const location = useLocation();

  if (!isGuideHighlightRoute(location.pathname)) {
    return null;
  }

  return (
    <HighlightModeToggle
      mode={highlightMode}
      onToggle={onHighlightModeChange}
      className="HighlightModeToggle--floating"
      aria-label="Change guide highlight display"
      titlePrefix="Guide highlight display"
    />
  );
}


function TeamStatsAuto() {
  const { teamId: activeTeamId, loading, setTeamId } = useTeam();
  const statsLocation=useLocation();
  const teamId=new URLSearchParams(statsLocation.search).get("teamId")||activeTeamId;
  const {userData}=useAuth();
  if (loading) return <p>Loading…</p>;
  if (!teamId) {
    return (
      <div style={{ padding: "1rem" }}>
        <h2>No active team</h2>
        <p>
          Pick a team from the top bar, or <Link to="/join">join/create a team</Link> to view stats.
        </p>
      </div>
    );
  }
  return <TeamStatisticsWorkspace teamId={teamId} teamIds={userData?.teamIds||[]} onTeamChange={setTeamId}><TeamStatsPage fallbackTeamId={teamId}/></TeamStatisticsWorkspace>;
}

function TeamStatsSummaryAuto() {
  const { teamId: activeTeamId, loading, setTeamId } = useTeam();
  const statsLocation=useLocation();
  const teamId=new URLSearchParams(statsLocation.search).get("teamId")||activeTeamId;
  const {userData}=useAuth();

  if (loading) return <p>Loading…</p>;

  if (!teamId) {
    return (
      <div style={{ padding: "1rem" }}>
        <h2>No active team</h2>
        <p>
          Pick a team from the top bar, or <Link to="/join">join/create a team</Link> to view Team Metrics Studio.
        </p>
      </div>
    );
  }

  return <TeamStatisticsWorkspace teamId={teamId} teamIds={userData?.teamIds||[]} onTeamChange={setTeamId}><TeamStatsSummary fallbackTeamId={teamId}/></TeamStatisticsWorkspace>;
}

function MyRoutineRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.delete("panel");
  const search = params.toString();

  return (
    <Navigate
      replace
      to={{
        pathname: "/my-routine",
        search: search ? `?${search}` : "",
        hash: location.hash,
      }}
    />
  );
}

function MainContent() {
  const location = useLocation();
  console.log("[RENDER] MainContent route:", location.pathname, location.search);

  useEffect(() => {
    console.log("[ROUTE] location changed:", location.pathname, location.search);
  }, [location.pathname, location.search]);
  const isChromeless = CHROMELESS_ROUTES.includes(location.pathname);
  const libraryParams=new URLSearchParams(location.search);
  const legacyLibraryTeam=libraryParams.get("team")||libraryParams.get("calendarTeam");
  if(/^\/(review|team-stats|library)(\/|$)/.test(location.pathname)&&legacyLibraryTeam&&!libraryParams.get("teamId")){libraryParams.set("teamId",legacyLibraryTeam);return <Navigate replace to={{pathname:location.pathname,search:"?"+libraryParams,hash:location.hash}}/>;}

  if (isSocialsPath(location.pathname)) {
    return <main><SocialsPage /></main>;
  }

  return (
    <div
      className={`Main__content__column
        ${isChromeless ? "Main__content__column--home" : "with-new-sb"} 
        ${location.pathname === "/feedback" ? "Main__content__column--feedback" : ""}`}
    >
      <main
        className={`Main__content__box ${
          location.pathname === "/login" ? "login-page" : ""
        } ${location.pathname === "/feedback" ? "Main__content__box--feedback" : ""}`}
      >
        <Routes location={location} key={getRouteMountKey(location)}>
          <Route path="/" element={<Home />} />
          <Route path="/labs/youtube-workspace" element={<RequireAuthPage><React.Suspense fallback={<p role="status">Loading YouTube workspace…</p>}><YouTubeWorkspace/></React.Suspense></RequireAuthPage>}/>
          {['/home','/coach','/manager'].map(path=><Route key={path} path={path} element={<RequireAuthPage><HomeWorkspace/></RequireAuthPage>}/>)}
          {LEGACY_HOME_PATHS.map(path=><Route key={path} path={path} element={<HomeWorkspaceRedirect/>}/>)}
          <Route
            path="/team-center"
            element={
              <Navigate
                replace
                to={{
                  pathname: "/team-center/dashboard",
                  search: location.search,
                  hash: location.hash,
                }}
              />
            }
          />
          <Route path="/team-center/dashboard" element={<RequireAuthPage><React.Suspense fallback={<p role="status">Loading team workspace…</p>}><TeamHubPage/></React.Suspense></RequireAuthPage>} />
          <Route path="/team-center/settings" element={<TeamDashboardPage/>}/>
          <Route
            path="/team-center/dashboard/expectations"
            element={
              <TeamHubRedirect tool="expectations"/>
            }
          />
          <Route
            path="/team-center/prep"
            element={<PrepCenterPage />}
          />
          {TEAM_PREPARATION_PAGES.map(page => <Route key={page.path} path={page.path} element={<PrepCenterPage />}/>)}
          {LEGACY_PREP_PATHS.filter(path=>!LEGACY_HOME_PATHS.includes(path)).map(path => <Route key={path} path={path} element={<PrepCenterPage />}/>)}
          <Route
            path="/team-dashboard"
            element={
              <Navigate
                replace
                to={{
                  pathname: "/team-center/dashboard",
                  search: location.search,
                  hash: location.hash,
                }}
              />
            }
          />
          <Route
            path="/team-dashboard/expectations"
            element={
              <Navigate
                replace
                to={{
                  pathname: "/team-center/dashboard/expectations",
                  search: location.search,
                  hash: location.hash,
                }}
              />
            }
          />
          <Route
            path="/prep"
            element={
              <Navigate
                replace
                to={{
                  pathname: "/team-center/prep",
                  search: location.search,
                  hash: location.hash,
                }}
              />
            }
          />
          <Route
            path="/my-prep"
            element={<MyRoutineRedirect />}
          />
          {/* <Route path="/index" element={<Index />} /> */}
          <Route path="/login" element={<Login />} />
          <Route path="/my-routine" element={<MyPrepPage />} />
          <Route path="/desktop-auth/google" element={<DesktopGoogleSignIn />} />
          <Route path="/signup" element={<Signup />} />
          <Route
            path="/account"
            element={
              <RequireAuthPage>
                <Account2 />
              </RequireAuthPage>
            }
          />
          <Route
            path="/billing"
            element={
              <RequireAuthPage>
                <Billing />
              </RequireAuthPage>
            }
          />
          <Route
            path="/join"
            element={
              <JoinTeam />
            }
          />
          <Route path="/verify" element={<VerifyEmail />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route
            path="/pick-username"
            element={
              <RequireAuthPage>
                <PickUsername />
              </RequireAuthPage>
            }
          />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/riot-review" element={<RiotReviewPage />} />
          <Route path="/desktop-app/download" element={<DesktopDownloadPage />} />
          <Route path="/pc-app/download" element={<Navigate to="/desktop-app/download" replace />} />
          <Route path="/clips" element={<PublicClipsPage />} />
          <Route path="/clips/:clipId" element={<PublicClipDetailPage />} />
          <Route path="/admin/strats-planner" element={<AdminStratsPlannerPage />} />

          <Route
            path="/team-setup-billing"
            element={
              <RequireTeamMembershipPage allowPendingBillingAccess>
                <TeamSetupBilling />
              </RequireTeamMembershipPage>
            }
          />
          <Route
            path="/team-setup-payers"
            element={
              <RequireTeamMembershipPage allowPendingBillingAccess>
                <TeamSetupPayers />
              </RequireTeamMembershipPage>
            }
          />
          {/* <Route path="/team-setup-payment" element={<TeamSetupPayment />} /> */}

          <Route
            path="/feedback"
            element={
              <RequireAuthPage>
                <Feedback />
              </RequireAuthPage>
            }
          />
          <Route
            path="/lfg"
            element={
              <RequireAuthPage>
                <LFGPage />
              </RequireAuthPage>
            }
          />

          {/* Individual Playbook */}
          <Route path="/Bind-Map-Macro" element={<BindMapApproach />} />
          <Route path="/Bind-Gekko" element={<BindGekko />} />
          <Route path="/Split-Map-Macro" element={<SplitMapApproach />} />
          <Route path="/Haven-KJ" element={<HavenKJ />} />
          <Route path="/Corrode-Yoru" element={<CorrodeYoru />} />

          {/* Coordinated Playbook */}
          <Route path="/playbook/NRG2026BindComp" element={<NRG2026BindComp />} />

          <Route path="/playbook/Split-SEN-Comp" element={<SplitSENComp />} />
          <Route path="/playbook/Template" element={<TemplateChapter />} />

          {/* Core */}
          <Route
            path="/guide-library"
            element={
              <RequireTeamFeaturePage
                featureKey="notes"
                title="Guide Library requires a team plan with guide tools."
                description="This page is available once your active team is on Basic or higher."
              >
                <GuildeLibrary />
              </RequireTeamFeaturePage>
            }
          />
          <Route path="/open" element={<SharedContentLauncher landing/>}/>
          <Route path="/library" element={<RequireAuthPage><TeamLibraryPage/></RequireAuthPage>} />
          <Route
            path="/review"
            element={
              <RequireTeamFeaturePage
                featureKey="vodReviewer"
                title="VOD Review is not included in your active team plan."
                description="This page is available on Premier, Collegiate, and Pro."
              >
                <ReviewDashboard />
              </RequireTeamFeaturePage>
            }
          />
          <Route
            path="/review/:mapId"
            element={
              <RequireTeamFeaturePage
                featureKey="vodReviewer"
                title="VOD Review is not included in your active team plan."
                description="This page is available on Premier, Collegiate, and Pro."
              >
                <ReviewDashboard />
              </RequireTeamFeaturePage>
            }
          />
          <Route
            path="/review/:mapId/:videoId"
            element={
              <RequireTeamFeaturePage
                featureKey="vodReviewer"
                title="VOD Review is not included in your active team plan."
                description="This page is available on Premier, Collegiate, and Pro."
              >
                <ReviewDashboard />
              </RequireTeamFeaturePage>
            }
          />

          {/* Team stats */}
          <Route
            path="/team-stats"
            element={
              <RequireTeamFeaturePage
                featureKey="teamStats"
                title="Team Stats is not included in your active team plan."
                description="This page is available once your active team is on Basic or higher."
              >
                <TeamStatsAuto />
              </RequireTeamFeaturePage>
            }
          />
          <Route
            path="/team-stats/summary"
            element={<Navigate to="/team-stats/studio" replace />}
          />
          <Route
            path="/team-stats/studio"
            element={
              <RequireTeamFeaturePage
                featureKey="teamStats"
                title="Team Metrics Studio is not included in your active team plan."
                description="This page is available once your active team is on Basic or higher."
              >
                <TeamStatsSummaryAuto />
              </RequireTeamFeaturePage>
            }
          />





          
        </Routes>
      </main>
    </div>
  );
}


export default App;
